/**
 * Created by Qingger Corp.
 * User: jsspf
 * Date: 2017/11/21
 * Time: 18:34
 */
import isString from 'lodash/isString';
import hash from 'object-hash';
import moment from 'moment';
import { CacheKeyType } from './types';
import { AppExceptionError } from '@/exception/AppException';
import { ErrCode } from '@/exception/ExceptionCode';
import cacheManager from '@/library/cache/storageCacheManager';

type CacheKeyTypes = string | ItemObject | Array<any>;
export type CacheSetFun = (data: any) => any;

/**
 * 缓存中存储的结构体
 */
interface CacheInStore {
  saveTime: number;
  expireTime: number;
  saveValue: any;
}

/**
 * 缓存管理类(单例)
 */
class StoreCacheManager {
  protected defaultApp: string;
  // protected storeItems    : ItemObject ;
  protected cacheStorage: Storage = null;

  private static __instance: StoreCacheManager = null;

  /**
   * 获得单例应用
   * @returns {StorageCache.CacheManager}
   * @constructor
   */
  public static GetInstance(): StoreCacheManager {
    if (StoreCacheManager.__instance == null) {
      StoreCacheManager.__instance = new StoreCacheManager();
    }
    return StoreCacheManager.__instance;
  }

  private constructor() {
    this.defaultApp = 'IPSMaintence';
    if (!window || !window.localStorage) {
      throw new AppExceptionError({
        code: ErrCode.ERR_DATA_OBJECT_NOT_EXIST,
        message: 'Windows LocalStorage对象不存在',
      });
    }
    this.cacheStorage = window.localStorage;
  }

  /**
   * 清除所有缓存
   */
  public clearAllItems(): void {
    this.cacheStorage.clear();
    // this.storeItems = {};
  }

  /**
   * 将不同的key变成字符串形式
   * @param {CacheKeyType} key
   *   1.对于字符串类型的Key，直接使用
   *   2.对于对象/数组类型的key,转换成hash值后使用
   * @returns {string}
   */
  private resolveKey(key: CacheKeyTypes): string {
    return isString(key) ? key : hash(key);
  }

  /**
   *
   * @param {CacheKeyType} key
   * @returns {ItemObject}
   */
  public forgetItem(key: CacheKeyTypes): void {
    const storeKey = this.resolveKey(key);
    this.cacheStorage.removeItem(storeKey);
    // delete (this.storeItems[storeKey]);
  }

  /**
   * 获得缓存中的数据
   * @param {CacheKeyType} key
   * @returns {any}
   */
  public getItem(key: CacheKeyTypes): any {
    const storeKey = this.resolveKey(key);
    const storeDataString = this.cacheStorage.getItem(storeKey);
    if (!storeDataString) {
      return null;
    }

    try {
      const currentTime = moment().unix();
      const storeData = JSON.parse(storeDataString) as CacheInStore;
      if (!storeData.saveValue) {
        return null;
      }

      if (storeData.expireTime != 0 && storeData.expireTime < currentTime) {
        this.forgetItem(key);
        return null;
      }

      return storeData.saveValue;
    } catch {
      this.forgetItem(key);
      return null;
    }
  }

  /**
   * 存储缓存数据
   * @param {CacheKeyType} key
   * @param value
   * @param {number} expires
   * @returns {ItemObject}
   */
  public setItem(key: CacheKeyTypes, value: any, expires = 3600): ItemObject {
    // return this.putItemWithObjectKey({key},value,tag);
    if (!key) {
      return {};
    }

    const storeKey = this.resolveKey(key);

    const saveTime = moment().unix();
    const expireTime = expires ? saveTime + expires : 0;

    const storeData = {
      saveTime,
      expireTime,
      saveValue: value,
    };

    // this.storeItems[storeKey] = storeData;
    this.cacheStorage.setItem(storeKey, JSON.stringify(storeData));

    return storeData;
  }

  /**
   * 异步缓存取得接口
   * @param {CacheKeyType} key
   * @param {Promise<any>} funCallback
   * @param {number} timeout
   * @returns {Promise<any>}
   */
  public cacheItemRemember(key: CacheKeyTypes, funCallback: Promise<any>, timeout = 3600): Promise<any> {
    const storeData = this.getItem(key);
    if (storeData) {
      return Promise.resolve(storeData);
    } else {
      const self = this;
      return funCallback.then(
        (resData) => {
          self.setItem(key, resData, timeout);
          return resData;
        },
        (err) => {
          Promise.reject(err);
        },
      );
    }
  }

  public getCacheLoader(cacheItem: CacheKeyTypes) {
    const self = this;
    return function <T = any>(): T {
      return self.getItem(cacheItem);
    };
  }

  public setCacheLoader(cacheItem: CacheKeyTypes, expires = 3600) {
    const self = this;
    return function(data: any) {
      return self.setItem(cacheItem, data, expires);
    };
  }
}

export default StoreCacheManager.GetInstance();
export const getActId = cacheManager.getCacheLoader(CacheKeyType.CACHE_FOR_ACT_ID);
export const setActId: CacheSetFun = cacheManager.setCacheLoader(CacheKeyType.CACHE_FOR_ACT_ID, 3600);
export const getEmpInfoCache = cacheManager.getCacheLoader(CacheKeyType.CACHE_FOR_EMPINFO);
export const setEmpInfoCache: CacheSetFun = cacheManager.setCacheLoader(CacheKeyType.CACHE_FOR_EMPINFO, 0);
